<template>
  <div
    ref="bannerEl"
    data-testid="team-banner"
    :class="[
      classes.container,
      alignment,
      { [classes.noName]: !name },
      'banner'
    ]"
  >
    <svg
      viewBox="0 0 127 24"
      :class="[classes.banner, 'banner-svg']"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      :preserveAspectRatio="customSvgAspectRatio || 'none'"
    >
      <path :d="customSvgPath || drawnPath" :fill="fill" />
    </svg>

    <TeamBadge
      v-if="teamBadgesEnabled && alignment === 'left'"
      :class="['badge', classes.badge, classes.badgeLeft]"
      :src="badge"
      :countryCode="countryCode"
      :defaultBadge="homeOrAwayTeam"
      :alt="name + ' badge'"
    />

    <TeamJersey
      v-if="teamJerseysEnabled && alignment === 'left'"
      :class="['jersey', classes.jersey, classes.jerseyLeft]"
      transform="scale(-1, 1)"
      :homeOrAwayTeam="homeOrAwayTeam"
      padding="2px"
    />

    <TeamBannerText
      :name="name"
      :textColor="teamColorsAvailable ? textColor : undefined"
    />

    <TeamBadge
      v-if="teamBadgesEnabled && alignment === 'right'"
      :class="['badge', classes.badge, classes.badgeRight]"
      :src="badge"
      :countryCode="countryCode"
      :defaultBadge="homeOrAwayTeam"
    />

    <TeamJersey
      v-if="teamJerseysEnabled && alignment === 'right'"
      :class="['jersey', classes.jersey, classes.jerseyRight]"
      :homeOrAwayTeam="homeOrAwayTeam"
    />
  </div>
</template>
<script lang="ts">
import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { computed, defineComponent, PropType } from "vue";
import TeamBadge from "./TeamBadge.vue";
import TeamJersey from "./team-jerseys/TeamJerseySelector.vue";
import TeamBannerText from "./TeamBannerText.vue";
import { Brand } from "@fresh8/types/src";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";
import { injectCarouselContext } from "../lib/provideInjectors";
import { injectTeamColors, injectUseTeamBadges } from "../lib/teamBranding";
import { injectUseTeamJerseys } from "../lib/teamJerseys";

const useStyles = createUseStyles({
  container: {
    position: "relative",
    boxSizing: "border-box",
    display: "flex",
    height: props => props.height + "px",
    alignSelf: "center",
    minWidth: "127px",
    alignItems: "center",
    width: "100%",
    "&.right": {
      justifyContent: "flex-end",
      paddingLeft: "7%"
    },
    "&.left": {
      paddingRight: "7%"
    }
  },
  banner: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    height: "100%",
    width: "100%",
    "&.left": {
      justifyContent: "flex-start"
    },
    "&.right": {
      justifyContent: "flex-end"
    }
  },

  noName: {
    justifyContent: "center !important",

    "& $badge": {
      width: "56px"
    },

    "& $jersey": {
      width: "56px"
    }
  },

  badge: {
    zIndex: 0,
    minWidth: "24px",
    width: "110%"
  },
  badgeLeft: {
    marginLeft: "6%",
    justifySelf: "flex-start"
  },
  badgeRight: {
    marginRight: "6%",
    justifySelf: "flex-end"
  },

  jersey: {
    zIndex: 0,
    minWidth: "24px",
    width: "110%"
  },
  jerseyLeft: {
    marginLeft: "6%",
    justifySelf: "flex-start"
  },
  jerseyRight: {
    marginRight: "6%",
    justifySelf: "flex-end"
  }
});

export default defineComponent({
  name: "TeamBanner",
  components: {
    TeamBadge,
    TeamJersey,
    TeamBannerText
  },
  props: {
    eventId: {
      type: String,
      required: true
    },
    alignment: {
      type: String as PropType<"left" | "right" | "center">,
      default: "left"
    },
    slope: {
      type: String as PropType<"top" | "bottom">,
      default: "top"
    },
    name: {
      type: String,
      required: false,
      default: ""
    },
    badge: {
      type: String,
      default: undefined
    },
    countryCode: {
      type: String,
      default: undefined,
      required: false
    },
    height: {
      type: Number,
      required: false,
      default: 0
    },
    customSvgPath: {
      type: String,
      default: undefined,
      required: false
    },
    customSvgAspectRatio: {
      type: String,
      default: undefined,
      required: false
    },
    disableBadge: {
      type: Boolean,
      default: false,
      required: false
    },
    homeOrAwayTeam: {
      type: String as PropType<"home" | "away">,
      default: "home",
      required: true
    }
  },
  setup(props) {
    const brandContext = injectBrand() || ({} as BrandContext);
    const teamColorsContext = injectTeamColors();

    const teamBadgesEnabled = computed(
      () => !props.disableBadge && injectUseTeamBadges().value
    );

    const teamJerseysEnabled = computed(
      () => !props.disableBadge && injectUseTeamJerseys().value
    );

    const teamColours = computed(
      () => teamColorsContext.colors.value[props.homeOrAwayTeam]
    );

    const { targetEl: bannerEl } = composePopoverForm("Team Banner", [
      {
        type: "color",
        name: "Banner background color",
        get: () =>
          brandContext?.brand.value?.adTemplate?.team?.banner
            ?.backgroundColor ||
          Object.keys(brandContext?.brand.value?.colors || [])[0],
        set: (value: string) => {
          if (brandContext.brand.value) {
            brandContext.brand.value.adTemplate.team.banner.backgroundColor = value;
          }
        }
      },
      {
        type: "color",
        name: "Badge border color",
        get: () =>
          brandContext?.brand.value?.adTemplate?.team?.badge?.borderColor ||
          Object.keys(brandContext?.brand.value?.colors || {})[0],
        set: (value: string) => {
          if (brandContext.brand.value) {
            brandContext.brand.value.adTemplate.team.badge.borderColor = value;
          }
        }
      },
      {
        type: "font",
        get: () => {
          return (
            brandContext?.brand.value?.adTemplate?.team?.banner?.font ||
            Object.keys(brandContext?.brand.value?.fonts || {})[0]
          );
        },
        set: (value: string) => {
          if (brandContext.brand.value) {
            brandContext.brand.value.adTemplate.team.banner.font = value;
          }
        }
      }
    ]);

    const drawnPath = computed(() => {
      switch ([props.alignment, props.slope].join(" ")) {
        case "left top":
          return "M0 0v24h127L116.6 0H0z";
        case "left bottom":
          return "M0 24V0h127l-10.4 24H0z";
        case "right top":
          return "M127 0v24H0L10.4 0H127z";
        case "right bottom":
          return "M127 24V0H0l10.4 24H127z";
        default:
          return "";
      }
    });

    const carouselContext = injectCarouselContext();

    const reactiveStyles = computed(() => ({
      isCarousel: carouselContext.value.isCarousel,
      height: props.height
    }));

    const classes = useStyles(reactiveStyles);

    const fill = computed(() => {
      if (teamColorsContext.teamColorsAvailable.value && teamColours.value) {
        return teamColours.value.banner.background;
      } else {
        return brandContext?.teamBranding.value.banner.backgroundColor;
      }
    });

    const textColor = computed(() => {
      if (teamColorsContext.teamColorsAvailable.value) {
        return teamColours.value?.banner.text;
      }

      return brandContext.getFontStyle(
        brandContext?.teamBranding.value.banner.font
      ).color;
    });

    return {
      bannerEl,
      classes,
      drawnPath,
      fill,
      textColor,
      teamColorsAvailable: teamColorsContext.teamColorsAvailable,
      teamBadgesEnabled,
      teamJerseysEnabled,
      teamBanner: {
        get: (brand: Brand) => brand.adTemplate.team.banner.font,
        set: (brand: Brand, value: string) =>
          (brand.adTemplate.team.banner.font = value)
      }
    };
  }
});
</script>
